import { Location } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { CartaPorteService } from 'src/app/services/carta-porte.service';
import { OrderService } from 'src/app/services/order.service';
import { TrailerService } from 'src/app/services/trailer.service';
import { UtilsService } from 'src/app/services/utils.service';

@Component({
  selector: 'app-carta-porte-view',
  templateUrl: './carta-porte-view.component.html',
  styleUrls: ['./carta-porte-view.component.css']
})
export class CartaPorteViewComponent {
  cartaPorte: any = null;
  selectedService: any = null;
  selectedTrailer: any = null;

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private spinner: NgxSpinnerService,
    private apiCartaPorte: CartaPorteService,
    private apiOrder: OrderService,
    private apiTrailer: TrailerService,
    private utils: UtilsService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const id = Number(params['id']);
      if (id) {
        this.loadCartaPorte(id);
      }
    });
  }

  goBack() {
    this.location.back();
  }

  get originAddress(): any {
    return this.cartaPorte?.origin_address || null;
  }

  get destinationAddress(): any {
    return this.cartaPorte?.destination_address || null;
  }

  get traslado(): any {
    return this.cartaPorte?.cfdi_taxes?.Traslados?.[0] || null;
  }

  get insurerName(): string {
    return this.cartaPorte?.cargo_insurer?.name || 'N/A';
  }

  get serviceFolio(): string {
    return this.selectedService?.folio || this.cartaPorte?.service?.folio || this.route.snapshot.queryParamMap.get('folio') || 'N/A';
  }

  get transportMeanLabel(): string {
    const key = this.cartaPorte?.transport_mean?.key;
    const name = this.cartaPorte?.transport_mean?.name;
    return [key, name].filter(Boolean).join(' - ') || 'N/A';
  }

  get vehicleConfigurationLabel(): string {
    const code = this.selectedService?.unit?.vehicle_configuration?.code;
    const description = this.selectedService?.unit?.vehicle_configuration?.description;
    return [code, description].filter(Boolean).join(' - ') || 'N/A';
  }

  get permitLabel(): string {
    const key = this.selectedService?.unit?.permit_type?.key;
    const description = this.selectedService?.unit?.permit_type?.description;
    return [key, description].filter(Boolean).join(' - ') || 'N/A';
  }

  getAddressValue(address: any, key: string, fallbackKey?: string): string {
    return address?.[key] || (fallbackKey ? address?.[fallbackKey] : '') || 'N/A';
  }

  downloadXml() {
    const cartaPorteId = Number(this.cartaPorte?.id);
    if (!cartaPorteId) {
      return;
    }

    this.spinner.show();
    this.apiCartaPorte.downloadCfdiXml(cartaPorteId).subscribe((xml: string) => {
      this.spinner.hide();

      const blob = new Blob([xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cfdi-carta-porte-${cartaPorteId}.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, (err: any) => {
      this.spinner.hide();
      const msg = err?.error?.message || 'No se pudo descargar el XML para esta carta porte';
      this.utils.alertSwal({ title: 'Advertencia', text: msg, icon: 'warning' });
    });
  }

  downloadPdf() {
    const cartaPorteId = Number(this.cartaPorte?.id);
    if (!cartaPorteId) {
      return;
    }

    this.spinner.show();
    this.apiCartaPorte.downloadPdf(cartaPorteId).subscribe((blob: Blob) => {
      this.spinner.hide();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cfdi-carta-porte-${cartaPorteId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, (err: any) => {
      this.spinner.hide();
      let msg = 'No se pudo generar el PDF para esta carta porte';

      if (err?.error instanceof Blob) {
        err.error.text().then((text: string) => {
          try {
            const parsed = JSON.parse(text);
            this.utils.alertSwal({ title: 'Advertencia', text: parsed?.message || msg, icon: 'warning' });
          } catch {
            this.utils.alertSwal({ title: 'Advertencia', text: msg, icon: 'warning' });
          }
        });
        return;
      }

      msg = err?.error?.message || msg;
      this.utils.alertSwal({ title: 'Advertencia', text: msg, icon: 'warning' });
    });
  }

  private loadCartaPorte(id: number) {
    this.spinner.show();
    this.apiCartaPorte.getId(id).subscribe((resp: any) => {
      this.cartaPorte = resp || null;

      const serviceId = Number(this.cartaPorte?.service?.id || this.cartaPorte?.service_id || 0);
      if (!serviceId) {
        this.spinner.hide();
        return;
      }

      this.apiOrder.getId(serviceId).subscribe((serviceResp: any) => {
        this.selectedService = serviceResp || this.cartaPorte?.service || null;
        this.loadTrailerForSelectedService();
        this.spinner.hide();
      }, () => {
        this.selectedService = this.cartaPorte?.service || null;
        this.spinner.hide();
      });
    }, () => {
      this.spinner.hide();
    });
  }

  private loadTrailerForSelectedService() {
    const requiresTrailer = !!(this.selectedService?.requires_trailer === true || this.selectedService?.requires_trailer === 1);
    const trailerId = Number(this.selectedService?.trailer_id);

    if (!requiresTrailer || !trailerId) {
      this.selectedTrailer = null;
      return;
    }

    this.apiTrailer.getId(trailerId).subscribe((resp: any) => {
      this.selectedTrailer = resp || null;
    }, () => {
      this.selectedTrailer = null;
    });
  }
}
